import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";

const TimeRangePicker = ({ startTime, endTime, onChange }) => {
  const parseTime = (timeStr) => {
    const [hour, minute] = timeStr.split(":");
    return { hour, minute };
  };

  const [isOpen, setIsOpen] = useState(false);
  const [internalStartTime, setInternalStartTime] = useState(
    parseTime(startTime)
  );
  const [internalEndTime, setInternalEndTime] = useState(parseTime(endTime));
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    setInternalStartTime(parseTime(startTime));
    setInternalEndTime(parseTime(endTime));
  }, [startTime, endTime]);

  const startHourRef = useRef(null);
  const startMinuteRef = useRef(null);
  const endHourRef = useRef(null);
  const endMinuteRef = useRef(null);
  const hourOptionRefs = useRef({});
  const minuteOptionRefs = useRef({});
  const buttonRef = useRef(null);

  const hours = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, "0")
  );
  const minutes = ["00", "15", "30", "45"];
  const itemHeight = 36;
  const visibleItems = 5;
  const listHeight = visibleItems * itemHeight;
  const paddingItems = visibleItems * 2;
  const paddedHours = [...hours, ...Array(paddingItems).fill("")];
  const paddedMinutes = [...minutes, ...Array(paddingItems).fill("")];

  useEffect(() => {
    if (isOpen && buttonRef.current && dropdownRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const shouldOpenUp =
        spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;
      setOpenUpward(shouldOpenUp);

      setDropdownPosition({
        top: shouldOpenUp
          ? rect.top + window.scrollY - dropdownHeight - 5
          : rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      scrollToTop("start", "hour", internalStartTime.hour);
      scrollToTop("start", "minute", internalStartTime.minute);
      scrollToTop("end", "hour", internalEndTime.hour);
      scrollToTop("end", "minute", internalEndTime.minute);
    }
  }, [isOpen, internalStartTime, internalEndTime]);

  const scrollToTop = (type, field, value) => {
    const container =
      type === "start"
        ? field === "hour"
          ? startHourRef.current
          : startMinuteRef.current
        : field === "hour"
        ? endHourRef.current
        : endMinuteRef.current;

    const refs =
      field === "hour" ? hourOptionRefs.current : minuteOptionRefs.current;
    const optionRef = refs[`${type}-${field}-${value}`];

    if (container && optionRef) {
      const containerTop = container.getBoundingClientRect().top;
      const optionTop = optionRef.getBoundingClientRect().top;
      const scrollAmount = optionTop - containerTop;

      container.scrollTo({
        top: container.scrollTop + scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleTimeSelect = (type, field, value) => {
    scrollToTop(type, field, value);

    if (type === "start") {
      setInternalStartTime((prev) => ({ ...prev, [field]: value }));
    } else {
      setInternalEndTime((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleApply = () => {
    const formatTime = (time) =>
      `${time.hour.padStart(2, "0")}:${time.minute.padStart(2, "0")}`;
    onChange([formatTime(internalStartTime), formatTime(internalEndTime)]);
    setIsOpen(false);
  };

  const setOptionRef = (ref, type, field, value) => {
    if (ref) {
      const refs =
        field === "hour" ? hourOptionRefs.current : minuteOptionRefs.current;
      refs[`${type}-${field}-${value}`] = ref;
    }
  };

  return (
    <Container>
      <ToggleButton
        type="button"
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
      >
        {`${internalStartTime.hour}:${internalStartTime.minute}`} -{" "}
        {`${internalEndTime.hour}:${internalEndTime.minute}`}
        <ClockIcon>🕒</ClockIcon>
      </ToggleButton>

      {isOpen &&
        ReactDOM.createPortal(
          <Dropdown
            ref={dropdownRef}
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            <TwoColumnGrid>
              <ColumnHeader>Hora de início</ColumnHeader>
              <ColumnHeader>Hora de fim</ColumnHeader>

              <DualPicker>
                <ScrollableList
                  ref={startHourRef}
                  style={{ height: `${listHeight}px` }}
                >
                  {paddedHours.map((hour, index) =>
                    hour ? (
                      <Option
                        key={`start-hour-${hour}-${index}`}
                        ref={(ref) => setOptionRef(ref, "start", "hour", hour)}
                        selected={hour === internalStartTime.hour}
                        onClick={() => handleTimeSelect("start", "hour", hour)}
                      >
                        {hour}
                      </Option>
                    ) : (
                      <EmptyOption key={`empty-start-hour-${index}`} />
                    )
                  )}
                </ScrollableList>

                <ScrollableList
                  ref={startMinuteRef}
                  style={{ height: `${listHeight}px` }}
                >
                  {paddedMinutes.map((minute, index) =>
                    minute ? (
                      <Option
                        key={`start-minute-${minute}-${index}`}
                        ref={(ref) =>
                          setOptionRef(ref, "start", "minute", minute)
                        }
                        selected={minute === internalStartTime.minute}
                        onClick={() =>
                          handleTimeSelect("start", "minute", minute)
                        }
                      >
                        {minute}
                      </Option>
                    ) : (
                      <EmptyOption key={`empty-start-minute-${index}`} />
                    )
                  )}
                </ScrollableList>
              </DualPicker>

              <DualPicker>
                <ScrollableList
                  ref={endHourRef}
                  style={{ height: `${listHeight}px` }}
                >
                  {paddedHours.map((hour, index) =>
                    hour ? (
                      <Option
                        key={`end-hour-${hour}-${index}`}
                        ref={(ref) => setOptionRef(ref, "end", "hour", hour)}
                        selected={hour === internalEndTime.hour}
                        onClick={() => handleTimeSelect("end", "hour", hour)}
                      >
                        {hour}
                      </Option>
                    ) : (
                      <EmptyOption key={`empty-end-hour-${index}`} />
                    )
                  )}
                </ScrollableList>

                <ScrollableList
                  ref={endMinuteRef}
                  style={{ height: `${listHeight}px` }}
                >
                  {paddedMinutes.map((minute, index) =>
                    minute ? (
                      <Option
                        key={`end-minute-${minute}-${index}`}
                        ref={(ref) =>
                          setOptionRef(ref, "end", "minute", minute)
                        }
                        selected={minute === internalEndTime.minute}
                        onClick={() =>
                          handleTimeSelect("end", "minute", minute)
                        }
                      >
                        {minute}
                      </Option>
                    ) : (
                      <EmptyOption key={`empty-end-minute-${index}`} />
                    )
                  )}
                </ScrollableList>
              </DualPicker>
            </TwoColumnGrid>

            <ApplyButton type="button" onClick={handleApply}>
              OK
            </ApplyButton>
          </Dropdown>,
          document.body
        )}
    </Container>
  );
};

export default TimeRangePicker;

// Styled Components

const Container = styled.div`
  position: relative;
  width: 100%;
  flex-shrink: 1;
`;

const ToggleButton = styled.button`
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #ee754c;
  border-radius: 4px;
  background: white;
  text-align: left;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ClockIcon = styled.span`
  font-size: 16px;
`;

const Dropdown = styled.div`
  position: absolute;
  max-width: 400px;
  min-width: 260px;
  padding: 15px;
  background: white;
  border: 1px solid #ee754c;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 15px;
`;

const DualPicker = styled.div`
  display: flex;
  gap: 8px;
`;

const ColumnHeader = styled.div`
  font-weight: bold;
  font-size: 14px;
  text-align: center;
  margin-bottom: 5px;
`;

const ScrollableList = styled.div`
  overflow-y: auto;
  scroll-behavior: smooth;
  width: 50%;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }

  scrollbar-color: rgba(0, 0, 0, 0.3) transparent;
  scrollbar-width: thin;
`;

const Option = styled.div`
  padding: 8px;
  text-align: center;
  cursor: pointer;
  background: ${(props) => (props.selected ? "#EE754C" : "transparent")};
  color: ${(props) => (props.selected ? "white" : "inherit")};
  border-radius: 4px;
  margin: 2px;

  &:hover {
    background: ${(props) => (props.selected ? "#EE561B" : "#f0f0f0")};
  }
`;

const EmptyOption = styled.div`
  visibility: hidden;
  height: 36px;
`;

const ApplyButton = styled.button`
  width: 100%;
  padding: 8px;
  background-color: #ee754c;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: #ee561b;
  }
`;
